import { TransformedSection } from 'components/ui/dataCapture/utils';
import { GenericInputProps } from 'components/ui/GenericInput';

export interface DataCaptureEditFormResponse {
  formName: string;
  formDescription: string;
  sections: DataCaptureEditFormSection[];
}

export interface DataCaptureEditFormSection {
  section_id: number;
  form_id: number;
  name: string;
  description: string;
  help_text: string;
  display_order: number;
  is_visible: boolean;
  created_at: Date | string;
  modified_at: Date | string;
  process_form_field: DataCaptureEditFormProcessFormField[];
}

export interface DataCaptureEditFormProcessFormField {
  form_field_id: number;
  form_id: number;
  process_field_id: number | null;
  custom_field_id: number | null;
  section_id: number;
  field_value: string | boolean;
  default_value: string;
  accepted_values: string[];
  metadata: DataCaptureEditFormMetadata;
  name: string;
  help_text: string;
}

export interface DataCaptureEditFormMetadata {
  field_id?: string;
  process_id: string;
  field_key: string;
  field_name: string;
  field_type: string;
  field_length: string;
  description: string;
  is_required: boolean;
  is_fda_required?: boolean;
  visible_on_form?: boolean;
  capture_type: string;
  validation_rules: string;
  help_text: string;
  display_order: number;
  is_system_field?: boolean;
  custom_field_id?: string;
  account_id?: string;
  is_visible?: boolean;
}

export type DataCaptureEditFormInputProps = Array<
  Omit<GenericInputProps<DataCaptureEditFormData>, 'control' | 'errors'>
>;

export type DataCaptureEditFormData = {
  formName: string;
  formDescription: string;
  sections: TransformedSection[];
};

export type DataCaptureListView = {
  form_id: string;
  form_name: string;
  template_name: string;
  created_by: string;
  created_at: string;
  updated_by: string;
  updated_at: string;
  status: string;
};

export interface GetDataCaptureFormByIdResponse {
  form_id: string;
  form_name: string;
  form_description: string;
  template_name: string;
  created_by: CreatedBy;
  updated_by: null | CreatedBy;
  created_at: string | Date;
  updated_at: string | Date;
  status: string;
  sections: DataCaptureEditFormSection[];
}

export interface CreatedBy {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export type SaveDataCaptureFormPayload = {
  sections: {
    fields: {
      form_field_id: number;
      field_name: string;
      help_text: string;
    }[];
    section_id: number;
    section_name: string;
    section_help_text: string;
  }[];
  formName: string;
  formDescription: string;
};

export enum ProcessFieldTypes {
  Color = 'color',
  Date = 'date',
  DateTimeLocal = 'datetime-local',
  Email = 'email',
  File = 'file',
  Hidden = 'hidden',
  Image = 'image',
  Month = 'month',
  Number = 'number',
  Password = 'password',
  Radio = 'radio',
  Range = 'range',
  Reset = 'reset',
  Search = 'search',
  Select = 'Select',
  Submit = 'submit',
  Tel = 'tel',
  Text = 'text',
  Time = 'time',
  Url = 'url',
  Week = 'week',
}

/******************************************
 * Backend / API Types for data fields configuration
 ******************************************/
export interface ProcessFormTemplate {
  form_id: string;
  form_name: string;
  form_description: string;
  template_name: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  status: string;
}

export interface ProcessFormTemplatesResponse {
  message: string;
  code: number;
  data: ProcessFormTemplate[];
}

export interface ReadOnlyValue<T> {
  value: T;
  read_only: boolean;
}

export interface ProcessFormTemplateFieldMetadata {
  field_name: string;
  is_required: ReadOnlyValue<boolean>;
  is_visible: ReadOnlyValue<boolean>;
  flow_through: ReadOnlyValue<boolean>;
  system_generated: ReadOnlyValue<boolean>;
  accepted_values: ReadOnlyValue<ProcessFormAcceptedValues>;
  default_values: { read_only: boolean; value?: string };
  field_type: string;
  capture_type: string | null;
  is_editable: boolean;
  values: any;
}

export interface ProcessFormTemplateField {
  id: string;
  name: string;
  is_custom: boolean;
  field_key: string;
  metadata: ProcessFormTemplateFieldMetadata;
}

export interface ProcessFormTemplateFieldsData {
  form_id: string;
  form_name: string;
  description: string;
  fields: ProcessFormTemplateField[];
  is_initial_process?: boolean;
}

export interface ProcessFormAcceptedFinalProduct {
  finalProductId: string;
  finalProductName: string;
  translationKey: string;
}

export interface ProcessFormAcceptedProduct {
  productId: string;
  productName: string;
  translationKey: string;
  finalProducts: ProcessFormAcceptedFinalProduct[];
}

export interface ProcessFormAcceptedVariety {
  varietyId: string;
  varietyName: string;
  translationKey: string;
  products: ProcessFormAcceptedProduct[];
}

export interface ProcessFormAcceptedCommodity {
  commodityId: string;
  commodityName: string;
  translationKey: string;
  varieties: ProcessFormAcceptedVariety[];
}

export interface ProcessFormSelectOption {
  id: string;
  name: string;
  translationKey?: string;
}

export interface ProcessFormLocation {
  id: string;
  name: string;
  description?: string;
}

export interface ProcessFormAddress {
  id: string;
  name: string;
  fullAddress: string;
  locations?: ProcessFormLocation[];
}

export interface ProcessFormAddressAcceptedValues {
  addresses: ProcessFormAddress[];
}

export interface Process {
  id: string;
  name: string;
  locations?: ProcessFormLocation[];
}

export interface ProcessFormAddressWithProcesses {
  id: string;
  name: string;
  fullAddress: string;
  processes?: Process[];
}

export interface ProcessFormAddressAcceptedValuesWithDescription {
  addresses: ProcessFormAddressWithProcesses[];
}

/**
 * Accepted values can be one of the following types:
 */
export type ProcessFormAcceptedValues =
  | ProcessFormAcceptedCommodity[]
  | ProcessFormSelectOption[]
  | ProcessFormAddressAcceptedValues;

/******************************************
 * New Form Payload & Response Types
 ******************************************/

export interface NewFormEntity {
  entityId?: string;
  value: string | null;
}

export interface ProcessFormNewFormFieldOverride {
  // For custom fields, use customFieldId instead of templateFieldId.
  templateFieldId?: string;
  customFieldId?: string;
  isRequired?: boolean;
  customFieldName?: string;
  helpText?: string;
  defaultValue?: NewFormEntity | null;
  acceptedValues?: NewFormEntity[];
  isVisible?: boolean;
  isFlowThrough?: boolean;
}

export interface ProcessFormNewFormPayload {
  templateId: string;
  blockId: string;
  name: string;
  description?: string;
  userIds: string[];
  fieldOverrides: ProcessFormNewFormFieldOverride[];
}

export interface ProcessFormNewFormField {
  id: string;
  fieldName: string;
  customFieldName: string | null;
  isFlowThrough: boolean;
  canAutoGenerate: boolean;
  isVisible: boolean;
  isRequired: boolean;
  helpText: string;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProcessFormNewFormSection {
  id: string;
  name: string;
  helpText: string | null;
  description: string | null;
  displayOrder: number;
  isVisible: boolean;
  createdAt: string;
  updatedAt: string;
  fields: ProcessFormNewFormField[];
}

export interface ProcessFormNewFormResponseData {
  id: string;
  name: string;
  description: string;
  productType: string | null;
  active: boolean;
  isTemplate: boolean;
  createdAt: string;
  updatedAt: string;
  status: string;
  sections: ProcessFormNewFormSection[];
}

export interface ProcessFormNewFormResponse {
  message: string;
  code: number;
  data: ProcessFormNewFormResponseData;
}

/******************************************
 * UI / Helper Types for data fields
 ******************************************/

/**
 * DataFieldCols is the flat UI type for a field.
 * We “flatten” the nested metadata into top–level properties.
 */
export interface DataFieldCols {
  key: string;
  name: string;
  required: ReadOnlyValue<boolean>;
  visible: ReadOnlyValue<boolean>;
  flowThrough: ReadOnlyValue<boolean>;
  systemGenerated: ReadOnlyValue<boolean>;
  field_type: string;
  accepted_values: ReadOnlyValue<ProcessFormAcceptedValues>;
  acceptedValues: string[]; // IDs selected in the UI
  defaultValue?: string;
  default_values: { read_only: boolean; value?: string };
  field_key?: string;
  is_custom: boolean;
  is_editable: boolean;
}
